import React from 'react'
import { string, array, func } from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import ScrollAnimation from 'react-animate-on-scroll'
import LazyLoad from 'react-lazyload'

import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { white } from 'styles/colors'
import { square, cover } from 'styles/global'
import { H1, H2, H3, fontSizes, fontWeights } from 'styles/typography'
import Icon from 'components/Icon'
import Button from 'components/button/Button'

import RightArrowThin from 'icons/right-arrow-thin.svg'

const Container = styled.div`
  position: relative;
  background-color: #14191d;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Outer = styled.div`
  position: relative;
  display: block;
  width: 100%;
  background-image: url(${props => `${process.env.BASE_PATH}/images/home/offers/${props.device}-bg-mobile.jpg`});
  background-size: cover;
  background-position: center;
  padding-top: ${spacing.xLarge};
  padding-bottom: ${spacing.xLarge};
  flex: 1;

  ${media.small`
    padding-top: ${spacing.xxLarge};
    padding-bottom: ${spacing.xxLarge};
  `};

  ${media.medium`
    position: relative;
    height: 760px;
    background-image: url(${props => `${process.env.BASE_PATH}/images/home/offers/${props.device}-bg.jpg`});
  `};

  ${media.large`
    padding-top: ${spacing.xxxLarge};
    padding-bottom: ${spacing.xLarge};
    height: 860px;
  `};

  ${media.xLarge`
    height: 900px;
  `};

  ${media.xxLarge`
    height: 1100px;
  `};
`

const Inner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 30;
  padding-left: 5%;
  padding-right: 5%;
  text-align: center;
  padding-bottom: ${spacing.large};

  ${media.xxLarge`
    padding-bottom: 1.5vw;
  `};
`

const Content = styled.div`
  position: relative;
`

const DeviceWrap = styled.div`
  position: relative;
  width: 88px;
  display: block;
  margin-left: auto;
  margin-right: auto;

  ${media.small`
    width: 100px;
  `};

  ${media.medium`
    width: 120px;
  `};

  ${media.large`
    width: 140px;
  `};

  ${media.xLarge`
    width: 160px;
  `};

  ${media.xxLarge`
    width: 190px;
  `};
`

const Device = styled.img`
  width: 100%;
`

const Slant = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  clip-path: polygon(0 43%, 100% 0, 100% 100%, 0% 100%);
  background-image: ${props => props.gradient};
  z-index: 20;

  ${media.medium`
    height: 70%;
  `};

  ${media.large`
    height: 85%;
    clip-path: polygon(100% 0, 100% 100%, 0% 100%);
  `};

  ${media.xLarge`
    height: 90%;
  `};
`

const Heading = styled(H1)`
  color: ${white};
  font-weight: ${fontWeights.black};
  margin-bottom: ${spacing.medium};
  font-size: 56px;

  ${media.small`
    font-size: 64px;
  `};

  ${media.medium`
    font-size: 72px;
    margin-bottom: 0.1em;
  `};

  ${media.large`
    font-size: 80px;
  `};

  ${media.xLarge`
    font-size: 84px;
  `};
`

const Subheading = styled(H3)`
  color: ${white};
  font-weight: ${fontWeights.regular};
  letter-spacing: 0.1em;
  margin-bottom: ${spacing.large};

  ${media.medium`
    margin-bottom: 0.75em;
  `};
`

const Meta = styled.span`
  display: block;
  text-align: center;
  color: ${white};
  margin-top: ${spacing.medium};

  ${media.xLarge`
    font-size: 18px;
  `};
`

const Details = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.medium`
    position: static;
  `};
`

const ActionWrap = styled.div`
  position: static;
  text-align: center;

  ${mediaDown.medium`
    background-color: ${props => props.accentColor};
    padding: ${spacing.medium};
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    backface-visibility: hidden;
  `};

  ${media.medium`
    position: absolute;
    top: ${spacing.xLarge};
    right: ${spacing.xLarge};
    z-index: 20;
  `};

  ${media.large`
    top: ${spacing.xxLarge};
    right: ${spacing.xxLarge};
  `};

  ${media.xLarge`
    top: ${spacing.xxxLarge};
    right: ${spacing.xxxLarge};
  `};
`

const ActionContainer = styled.button`
  color: ${white};
  font-size: ${fontSizes.tiny};
  letter-spacing: 0.15em;
  display: inline-flex;
  align-items: center;
  border-bottom: 1px solid;
  padding-bottom: ${spacing.tiny};
`

const ActionLabel = styled.span`
  margin-right: ${spacing.large};

  ${media.medium`
    margin-right: ${spacing.medium};
  `};
`

const Price = styled(H2)`
  display: block;
  color: ${white};
  margin: 0;

  ${media.medium`
    margin-right: ${spacing.large};
  `};
`

const Navigation = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`

const NavigateButtonContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid ${white};
  font-weight: ${fontWeights.medium};
  color: ${white};
  font-size: 8px;
  letter-spacing: 0.25em;
  ${square('100px')};

  & + & {
    margin-left: ${spacing.xxLarge};
  }
`

const NavigateButtonIcon = styled.img`
  width: 70px;
  margin-bottom: ${spacing.tiny};
`

const OverlayLink = styled(Link)`
  display: block;
  color: ${white};
  text-align: center;
  text-transform: uppercase;
  margin-bottom: ${spacing.medium};
  margin-top: -${spacing.small};
  font-size: ${fontSizes.tiny};
  letter-spacing: 0.15em;

  &:after {
    content: '';
    ${cover('absolute')};
    z-index: 40; /* Position above content */
  }
`

const FeaturedProduct = ({
  link,
  heading,
  subheading,
  meta,
  price,
  cta,
  navigation,
  handleCtaClick,
  accentColor,
  device,
  gradient
}) => (
  <Container as={ScrollAnimation} animateIn="fadeInUp" offset={0} animateOnce>
    <Outer device={device}>
      <OverlayLink to={link} className="u-hide-medium-up">
        Click to shop
      </OverlayLink>

      <DeviceWrap as={ScrollAnimation} animateIn="fadeInUp" offset={0} animateOnce>
        <LazyLoad>
          <Device src={`${process.env.BASE_PATH}/images/home/offers/${device}.png`} alt={heading} />
        </LazyLoad>
      </DeviceWrap>
      <Inner>
        <Content>
          <Heading as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={200} animateOnce>
            {heading}
          </Heading>
          <Subheading as={ScrollAnimation} animateIn="fadeInUp" offset={0} animateOnce delay={400}>
            {subheading}
          </Subheading>
          <Details as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={600} animateOnce>
            <Price as="span">{price}</Price>

            <Button
              to={link}
              theme="custom"
              variant="outline"
              customColor={white}
              size="large"
              className="u-hide-medium-down"
            >
              Buy now
            </Button>
          </Details>
          {meta && (
            <Meta as={ScrollAnimation} animateIn="fadeInUp" offset={0} animateOnce delay={800}>
              {meta}
            </Meta>
          )}
        </Content>
      </Inner>

      <Slant gradient={gradient} />
    </Outer>
    <ActionWrap accentColor={accentColor}>
      <Action label={cta} onClick={handleCtaClick} className="u-hide-medium-down" />
      <Navigation
        className="u-hide-medium-up"
        as={ScrollAnimation}
        animateIn="fadeInUp"
        offset={0}
        delay={800}
        animateOnce
      >
        {navigation.map((item, i) => (
          <NavigateButton key={i} label={item.label} onClick={item.onClick} icon={item.icon} />
        ))}
      </Navigation>
    </ActionWrap>
  </Container>
)

FeaturedProduct.propTypes = {
  link: string.isRequired,
  handleCtaClick: func.isRequired,
  heading: string.isRequired,
  subheading: string.isRequired,
  meta: string,
  price: string.isRequired,
  cta: string,
  accentColor: string.isRequired,
  navigation: array,
  device: string.isRequired,
  gradient: string.isRequired
}

export const Action = ({ label, onClick, className }) => (
  <ActionContainer onClick={onClick} className={className}>
    <ActionLabel>{label}</ActionLabel>
    <Icon icon={<RightArrowThin />} />
  </ActionContainer>
)

Action.propTypes = {
  label: string.isRequired,
  className: string.isRequired,
  onClick: func.isRequired
}

export const NavigateButton = ({ icon, label, onClick }) => (
  <NavigateButtonContainer onClick={onClick}>
    <NavigateButtonIcon src={`${process.env.BASE_PATH}/images/home/offers/icons/${icon}.png`} />
    {label}
  </NavigateButtonContainer>
)

NavigateButton.propTypes = {
  label: string.isRequired,
  onClick: func.isRequired,
  icon: string.isRequired
}

export default FeaturedProduct
